import { useEffect, useState, useMemo, useCallback } from "react";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import { getItem, deleteItem } from "../../Actions/ItemAction";
import { getCompany } from "../../Actions/CompanyAction";
import { CompanySearchField } from "../Common/CompanySearchField";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip, Box } from "@mui/material";
import Swal from "sweetalert2";
import { isUndefined, debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import MobileViewTabelList from "../Common/MobileViewTabelList";
import {
  isStockTracked,
  itemStockState,
  itemTypeLabel,
  stockChipStyle,
  formatQty,
  unitOf,
} from "../../global/stock";
import { SERIAL_LABEL } from "../../global/itemDisplay";

/**
 * The stock pill.
 *
 * Renders nothing for a service product, which is what keeps quantities off the
 * rows of items that do not have any - the rule lives here rather than in every
 * caller.
 */
const StockCell = ({ item }) => {
  const state = itemStockState(item);
  if (!state) return <span className="text-gray-400">-</span>;
  const style = stockChipStyle(state);
  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <span style={{ fontWeight: 700 }}>
        {formatQty(item.currentStock, unitOf(item))}
      </span>
      <span
        style={{
          backgroundColor: style.bg,
          color: style.fg,
          fontSize: 11,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 8,
          whiteSpace: "nowrap",
        }}
      >
        {style.label}
      </span>
    </Box>
  );
};

const ItemList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  // The Company search: {}, { companyId } or { companyName } (CompanySearchField).
  const [companyFilter, setCompanyFilter] = useState({});

  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const isGettingItemList = useSelector(
    (state) => state.ItemListReducer?.isGettingItemList,
  );
  const itemList = useSelector(
    (state) => state.ItemListReducer?.itemList || [],
  );
  const totalResults = useSelector(
    (state) => state.ItemListReducer?.totalResults || 0,
  );

  useEffect(() => {
    dispatch(
      getItem(
        pagination.pageIndex,
        pagination.pageSize,
        globalFilter,
        companyFilter,
      ),
    );
  }, [pagination, dispatch, globalFilter, companyFilter]);

  // The companies the Company search suggests.
  useEffect(() => {
    dispatch(getCompany("", "", ""));
  }, [dispatch]);

  // A new company search starts again from the first page.
  const handleCompanySearch = useCallback((filter) => {
    setCompanyFilter(filter);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value) => {
        setGlobalFilter(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  const handleDeleteItem = (row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "mui-swal-popup",
      },
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        if (container) {
          container.style.zIndex = "2000"; // higher than MUI
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          deleteItem(
            row.original._id,
            handleVisible,
            handleDangerVisible,
            pagination.pageIndex,
            pagination.pageSize,
            globalFilter,
            companyFilter,
          ),
        );
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 60,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) =>
          row.index + 1 + pagination.pageIndex * pagination.pageSize,
      },
      {
        accessorKey: "companyId.companyName",
        header: "Company Name",
        size: 150, // slightly wider
        grow: 1, // fixed width
        enableEditing: false,
      },
      {
        accessorKey: "name",
        header: "Item Name",
        size: 150,
        grow: 1, // take flexible space
        enableEditing: false,
      },
      {
        accessorKey: "serial",
        header: SERIAL_LABEL,
        size: 150,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "HSCodeId.pctCode",
        header: "Hs Code Name",
        size: 150,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "uom",
        header: "UOM",
        size: 150,
        grow: 1,
        enableEditing: false,
      },
      {
        accessorKey: "itemType",
        header: "Item Type",
        size: 140,
        grow: 1,
        enableEditing: false,
        Cell: ({ row }) => itemTypeLabel(row.original?.itemType),
      },
      {
        accessorKey: "currentStock",
        header: "Stock",
        size: 170,
        grow: 1,
        enableEditing: false,
        enableSorting: false,
        Cell: ({ row }) => <StockCell item={row.original} />,
      },
      {
        accessorKey: "description",
        header: "Item Description",
        size: 150,
        grow: 1, // take flexible space
        enableEditing: false,
      },
      {
        accessorKey: "createdBy.email",
        header: "Created By",
        grow: true,
        enableEditing: true,
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          placeholder: "Enter Created By Email",
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 97,
        grow: 0, // fixed width
        enableEditing: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Box display="flex" gap={2}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() =>
                  navigate("/main/edit-item", { state: row.original })
                }
                sx={{ color: "black" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteItem(row)}
                sx={{ color: "black" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line
    [pagination],
  );

  const renderMobileItemCards = () => {
    return itemList.map((item, index) => {
      const serial = index + 1 + pagination.pageIndex * pagination.pageSize;
      return (
        <Box
          key={item._id}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box sx={{ fontSize: 12, color: "#999" }}>#{serial}</Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => navigate("/main/edit-item", { state: item })}
                  sx={{ color: "black" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteItem({ original: item })}
                  sx={{ color: "black" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Company:</b>
            <Box sx={{ fontSize: 14 }}>
              {item?.companyId?.companyName || "-"}
            </Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Item Name:</b>
            <Box sx={{ fontSize: 14 }}>{item.name || "-"}</Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>
              {SERIAL_LABEL}:
            </b>
            <Box sx={{ fontSize: 14 }}>{item.serial || "-"}</Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>HS Code:</b>
            <Box sx={{ fontSize: 14 }}>{item?.HSCodeId?.pctCode || "-"}</Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>UOM:</b>
            <Box sx={{ fontSize: 14 }}>{item.uom || "-"}</Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Item Type:</b>
            <Box sx={{ fontSize: 14 }}>{itemTypeLabel(item.itemType)}</Box>
          </Box>
          {isStockTracked(item) && (
            <Box sx={{ mb: 1 }}>
              <b className={`text-[${globalStyle.bgColorBlue}]`}>Stock:</b>
              <Box sx={{ fontSize: 14, mt: 0.5 }}>
                <StockCell item={item} />
              </Box>
            </Box>
          )}
          <Box>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Description:</b>
            <Box sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
              {item.description || "-"}
            </Box>
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box className="p-4">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap", // important for mobile
          gap: 1,
        }}
      >
        <Box>
          <Box
            component="h1"
            sx={{
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 700,
              color: globalStyle.bgColorBlue,
              fontSize: {
                xs: globalStyle.headingFontSizeSmall, // mobile
                sm: globalStyle.headingFontSize, // tablet+
              },
              lineHeight: 1.2,
            }}
          >
            Items List
          </Box>

          {/* Optional subtitle (mobile friendly) */}
          <Box
            component="p"
            sx={{
              mt: 0.5,
              fontFamily: globalStyle.fontFamilyParagraph,
              fontSize: {
                xs: 12,
                sm: 14,
              },
              color: "#666",
            }}
          >
            Manage and view all Items records
          </Box>
        </Box>
        <CompanySearchField
          companies={companiesList}
          onSearch={handleCompanySearch}
        />
      </Box>
      {isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingItemList}
          searchLabel="Items"
        >
          {renderMobileItemCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={itemList}
          enableRowActions={false}
          manualPagination
          rowCount={totalResults}
          state={{
            columnOrder: [
              "_id",
              "companyId.companyName",
              "name",
              "serial",
              "HSCodeId.pctCode",
              "uom",
              "itemType",
              "currentStock",
              "description",
              "createdBy.email",
              "actions",
            ],
            columnPinning: {
              right: ["actions"],
            },
            isLoading: isGettingItemList,
            pagination,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          columnFilterDisplayMode={false}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              "&:hover": {
                backgroundColor: globalStyle.bgColor,
              },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none", // ✅ remove height restriction
              overflow: "visible", // ✅ allow content to expand
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiPaginationProps={{
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: "fixed",
            },
          }}
        />
      )}
    </Box>
  );
};

export default ItemList;
